/**
 * Redux Slice for Inspection Builder (New Implementation)
 *
 * Simplified state management without language dictionary indirection.
 * All text values are stored directly in fields.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { resetALL } from 'Redux/actions';
import {
  getPendingConfig,
  getActiveConfig as getPublishedConfig,
  saveConfiguration,
  deployConfiguration,
} from '../../../../../services-px/configApi';

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Load configuration from API
 * Gets companyId from Redux state (user.companyId)
 */
export const loadConfig = createAsyncThunk(
  'inspectionBuilder/loadConfig',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Get companyId from Redux state
      const companyId = getState().securityRedux?.user?.companyId;

      // Fetch pending and published configs
      const pendingConfig = await getPendingConfig(companyId);
      const publishedConfig = await getPublishedConfig();

      // If no pending config, use published as base
      let config = pendingConfig;
      if (!config && publishedConfig) {
        config = { ...publishedConfig, pending: false };
      }

      // If still no config, create empty one
      if (!config) {
        config = {
          version: '1.0',
          configType: 'evir',
          pending: false,
          zoneLayouts: [],
          inspectionDetails: [],
        };
      }

      return {
        config,
        deployedConfig: publishedConfig,
        // Check if loaded config is pending (has unsaved work waiting to be deployed)
        workInProgress: pendingConfig?.pending === true,
      };
    } catch (error) {
      const errorMessage =
        error.response?.status === 403
          ? 'Access denied. Your EVIR token may be expired or invalid.'
          : error.response?.status === 401
          ? 'Authentication required. Please log in.'
          : error.message || 'Failed to load configuration';
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Save configuration as draft
 */
export const saveConfig = createAsyncThunk(
  'inspectionBuilder/saveConfig',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { config } = getState().inspectionBuilderRedux;
      await saveConfiguration(config);
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save configuration');
    }
  }
);

/**
 * Deploy configuration
 */
export const deployConfig = createAsyncThunk(
  'inspectionBuilder/deployConfig',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { config } = getState().inspectionBuilderRedux;

      // Deploy the config to published
      await deployConfiguration(config);

      // Clear the pending state by saving with pending: false
      // This ensures loadConfig won't see stale pending state after deploy
      await saveConfiguration(config, { pending: false });

      return { config };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to deploy configuration');
    }
  }
);

/**
 * Discard pending changes and reload from deployed
 */
export const discardPendingConfig = createAsyncThunk(
  'inspectionBuilder/discardPendingConfig',
  async (_, { rejectWithValue }) => {
    try {
      const publishedConfig = await getPublishedConfig();

      if (!publishedConfig) {
        return rejectWithValue('No published configuration to restore');
      }

      // Overwrite pending config with published config and set pending: false to clear pending state
      await saveConfiguration(publishedConfig, { pending: false });

      return { config: publishedConfig };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to discard changes');
    }
  }
);

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  config: {
    id: null,
    version: '1.0',
    configType: 'evir',
    zoneLayouts: [],
    inspectionDetails: [],
  },
  originalConfig: null,
  deployedConfig: null,
  ui: {
    activeTab: 0,
    selectedLayoutId: null,
    selectedInspectionId: null,
    selectedFieldId: null,
    unsavedChanges: false,
    workInProgress: false,
  },
  loading: false,
  saving: false,
  deploying: false,
  error: null,
};

// ============================================================================
// Slice
// ============================================================================

const inspectionBuilderSlice = createSlice({
  name: 'inspectionBuilder',
  initialState,
  reducers: {
    // ------------------------------------
    // UI Selection Actions
    // ------------------------------------
    setActiveTab: (state, action) => {
      state.ui.activeTab = action.payload;
    },

    selectLayout: (state, action) => {
      state.ui.selectedLayoutId = action.payload;
    },

    selectInspection: (state, action) => {
      state.ui.selectedInspectionId = action.payload;
    },

    selectField: (state, action) => {
      state.ui.selectedFieldId = action.payload;
    },

    clearSelections: (state) => {
      state.ui.selectedLayoutId = null;
      state.ui.selectedInspectionId = null;
      state.ui.selectedFieldId = null;
    },

    // ------------------------------------
    // Change Tracking
    // ------------------------------------
    setUnsavedChanges: (state, action) => {
      state.ui.unsavedChanges = action.payload;
    },

    undoChanges: (state) => {
      if (state.originalConfig) {
        state.config = JSON.parse(JSON.stringify(state.originalConfig));
        state.ui.unsavedChanges = false;
      }
    },

    // ------------------------------------
    // Asset Layout Actions
    // ------------------------------------
    addAssetLayout: (state, action) => {
      const newLayout = action.payload;

      // Note: Duplicate asset type validation happens on save, not here
      // This matches ect2-web behavior - allow adding, validate on save

      // Add with new UUID
      const layoutWithId = {
        ...newLayout,
        layoutId: newLayout.layoutId || uuidv4(),
      };

      state.config.zoneLayouts.push(layoutWithId);
      state.ui.unsavedChanges = true;

      // Auto-select the new layout
      state.ui.selectedLayoutId = state.config.zoneLayouts.length - 1;
    },

    updateLayout: (state, action) => {
      const { layoutIndex, updates } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]) {
        state.config.zoneLayouts[layoutIndex] = {
          ...state.config.zoneLayouts[layoutIndex],
          ...updates,
        };
        state.ui.unsavedChanges = true;
      }
    },

    updateLayoutName: (state, action) => {
      const { layoutIndex, layoutName } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]) {
        state.config.zoneLayouts[layoutIndex].layoutName = layoutName;
        state.ui.unsavedChanges = true;
      }
    },

    cloneAssetLayout: (state, action) => {
      const { layoutIndex } = action.payload;
      const original = state.config.zoneLayouts[layoutIndex];

      if (!original) return;

      // Deep clone
      const cloned = JSON.parse(JSON.stringify(original));
      cloned.layoutId = uuidv4();
      cloned.layoutName = `${cloned.layoutName} (Copy)`;

      // Insert after original
      state.config.zoneLayouts.splice(layoutIndex + 1, 0, cloned);
      state.ui.unsavedChanges = true;

      // Select the cloned layout
      state.ui.selectedLayoutId = layoutIndex + 1;
    },

    deleteAssetLayout: (state, action) => {
      const { layoutIndex } = action.payload;
      state.config.zoneLayouts.splice(layoutIndex, 1);
      state.ui.unsavedChanges = true;

      // Clear selection if deleted layout was selected
      if (state.ui.selectedLayoutId === layoutIndex) {
        state.ui.selectedLayoutId = null;
      } else if (state.ui.selectedLayoutId > layoutIndex) {
        state.ui.selectedLayoutId--;
      }
    },

    // ------------------------------------
    // Zone Actions
    // ------------------------------------
    addZone: (state, action) => {
      const { layoutIndex, zone } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]) {
        state.config.zoneLayouts[layoutIndex].configZones.push(zone);
        state.ui.unsavedChanges = true;
      }
    },

    updateZone: (state, action) => {
      const { layoutIndex, zoneIndex, zone } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]?.configZones[zoneIndex]) {
        state.config.zoneLayouts[layoutIndex].configZones[zoneIndex] = zone;
        state.ui.unsavedChanges = true;
      }
    },

    deleteZone: (state, action) => {
      const { layoutIndex, zoneIndex } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]?.configZones) {
        state.config.zoneLayouts[layoutIndex].configZones.splice(zoneIndex, 1);
        state.ui.unsavedChanges = true;
      }
    },

    updateZonePosition: (state, action) => {
      const { layoutIndex, zoneIndex, newPosition } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]?.configZones[zoneIndex]) {
        state.config.zoneLayouts[layoutIndex].configZones[zoneIndex].assetViewLocation = newPosition;
        state.ui.unsavedChanges = true;
      }
    },

    cloneZone: (state, action) => {
      const { layoutIndex, zoneIndex } = action.payload;
      const layout = state.config.zoneLayouts[layoutIndex];
      const original = layout?.configZones[zoneIndex];

      if (!original) return;

      // Deep clone
      const cloned = JSON.parse(JSON.stringify(original));
      cloned.tag = `${cloned.tag} (Copy)`;

      // Find next available tagNumber
      const maxTagNumber = layout.configZones.reduce(
        (max, z) => Math.max(max, z.tagNumber || 0),
        0
      );
      cloned.tagNumber = maxTagNumber + 1;

      // Insert after original
      layout.configZones.splice(zoneIndex + 1, 0, cloned);
      state.ui.unsavedChanges = true;
    },

    // ------------------------------------
    // Component Actions
    // ------------------------------------
    addComponent: (state, action) => {
      const { layoutIndex, zoneIndex, component } = action.payload;
      const layout = state.config.zoneLayouts[layoutIndex];

      if (!layout) return;

      // Add component to layout's components array
      layout.components.push(component);
      const newComponentIndex = layout.components.length - 1;

      // Add index to zone's tagComponents
      if (layout.configZones[zoneIndex]) {
        layout.configZones[zoneIndex].tagComponents.push(newComponentIndex);
      }

      state.ui.unsavedChanges = true;
    },

    updateComponent: (state, action) => {
      const { layoutIndex, componentIndex, component } = action.payload;
      if (state.config.zoneLayouts[layoutIndex]?.components[componentIndex]) {
        state.config.zoneLayouts[layoutIndex].components[componentIndex] = component;
        state.ui.unsavedChanges = true;
      }
    },

    deleteComponent: (state, action) => {
      const { layoutIndex, zoneIndex, componentIndex } = action.payload;
      const layout = state.config.zoneLayouts[layoutIndex];

      if (!layout) return;

      // Remove component index from zone's tagComponents
      if (layout.configZones[zoneIndex]) {
        layout.configZones[zoneIndex].tagComponents = layout.configZones[
          zoneIndex
        ].tagComponents.filter((idx) => idx !== componentIndex);

        // Adjust indices for components after the deleted one
        layout.configZones[zoneIndex].tagComponents = layout.configZones[
          zoneIndex
        ].tagComponents.map((idx) => (idx > componentIndex ? idx - 1 : idx));
      }

      // Remove component from layout's components array
      layout.components.splice(componentIndex, 1);

      // Update all zones' tagComponents to reflect new indices
      layout.configZones.forEach((zone) => {
        zone.tagComponents = zone.tagComponents.map((idx) =>
          idx > componentIndex ? idx - 1 : idx
        );
      });

      state.ui.unsavedChanges = true;
    },

    cloneComponent: (state, action) => {
      const { layoutIndex, zoneIndex, tagComponentIndex } = action.payload;
      const layout = state.config.zoneLayouts[layoutIndex];
      const zone = layout?.configZones[zoneIndex];

      if (!zone) return;

      const componentIndex = zone.tagComponents[tagComponentIndex];
      const original = layout.components[componentIndex];

      if (!original) return;

      // Deep clone
      const cloned = JSON.parse(JSON.stringify(original));
      cloned.component = `${cloned.component} (Copy)`;

      // Add to components array
      layout.components.push(cloned);
      const newComponentIndex = layout.components.length - 1;

      // Add to zone's tagComponents after original
      zone.tagComponents.splice(tagComponentIndex + 1, 0, newComponentIndex);

      state.ui.unsavedChanges = true;
    },

    // ------------------------------------
    // Inspection Type Actions
    // ------------------------------------
    addInspectionType: (state, action) => {
      // Handle both { inspection: ... } and direct inspection object
      const inspection = action.payload.inspection || action.payload;

      // Add with new UUID if not present
      const inspectionWithId = {
        ...inspection,
        typeId: inspection.typeId || uuidv4(),
      };

      state.config.inspectionDetails.push(inspectionWithId);
      state.ui.unsavedChanges = true;

      // Auto-select the new inspection
      state.ui.selectedInspectionId = inspectionWithId.typeId;
    },

    createInspectionType: (state, action) => {
      const { name, description, category, categoryName, ...options } = action.payload;

      const newInspection = {
        typeId: uuidv4(),
        detailType: name,
        description: description,
        detailCategory: categoryName || category,
        categoryId: category,
        enforceOrder: options.enforceOrder || false,
        legallyMandated: options.legallyMandated || false,
        prepopulateDefects: options.prepopulateDefects || true,
        requiresReview: options.requiresReview || false,
        zoneless: options.zoneless || false,
        associateDriver: options.associateDriver || true,
        formData: [],
      };

      state.config.inspectionDetails.push(newInspection);
      state.ui.unsavedChanges = true;
      state.ui.selectedInspectionId = newInspection.typeId;
    },

    updateInspection: (state, action) => {
      // Handle both payload formats:
      // - { inspectionIndex, inspection } from InspectionTypesTab
      // - { inspectionId, updates } for direct updates
      const { inspectionIndex, inspection, inspectionId, updates } = action.payload;

      let index = inspectionIndex;
      let newData = inspection;

      // If using inspectionId format, find the index and use updates
      if (inspectionId !== undefined) {
        index = state.config.inspectionDetails.findIndex(
          (i) => i.typeId === inspectionId || i.detailType === inspectionId
        );
        newData = { ...state.config.inspectionDetails[index], ...updates };
      }

      if (index !== -1 && index !== undefined) {
        state.config.inspectionDetails[index] = newData;
        state.ui.unsavedChanges = true;
      }
    },

    deleteInspectionType: (state, action) => {
      const { inspectionId } = action.payload;
      state.config.inspectionDetails = state.config.inspectionDetails.filter(
        (i) => i.typeId !== inspectionId
      );
      state.ui.unsavedChanges = true;

      if (state.ui.selectedInspectionId === inspectionId) {
        state.ui.selectedInspectionId = null;
      }
    },

    // ------------------------------------
    // Form Field Actions
    // ------------------------------------
    addFormField: (state, action) => {
      const { inspectionId, field, index } = action.payload;
      const inspection = state.config.inspectionDetails.find(
        (i) => i.typeId === inspectionId || i.detailType === inspectionId
      );

      if (!inspection) return;

      const fieldWithId = {
        ...field,
        id: field.id || uuidv4(),
      };

      if (index !== undefined && index >= 0) {
        inspection.formData.splice(index, 0, fieldWithId);
      } else {
        inspection.formData.push(fieldWithId);
      }

      state.ui.unsavedChanges = true;
    },

    updateFormField: (state, action) => {
      // Handle both { fieldIndex } and { fieldId } payload formats
      const { inspectionId, fieldIndex, fieldId, updates } = action.payload;
      const inspection = state.config.inspectionDetails.find(
        (i) => i.typeId === inspectionId || i.detailType === inspectionId
      );

      if (!inspection?.formData) return;

      // Find index by fieldId if not provided directly
      let index = fieldIndex;
      if (index === undefined && fieldId) {
        index = inspection.formData.findIndex((f) => f.fieldId === fieldId);
      }

      if (index !== undefined && index !== -1 && inspection.formData[index]) {
        inspection.formData[index] = {
          ...inspection.formData[index],
          ...updates,
        };
        state.ui.unsavedChanges = true;
      }
    },

    removeFormField: (state, action) => {
      // Handle both { fieldIndex } and { fieldId } payload formats
      const { inspectionId, fieldIndex, fieldId } = action.payload;
      const inspection = state.config.inspectionDetails.find(
        (i) => i.typeId === inspectionId || i.detailType === inspectionId
      );

      if (!inspection?.formData) return;

      // Find index by fieldId if not provided directly
      let index = fieldIndex;
      if (index === undefined && fieldId) {
        index = inspection.formData.findIndex((f) => f.fieldId === fieldId);
      }

      if (index !== undefined && index !== -1) {
        inspection.formData.splice(index, 1);
        state.ui.unsavedChanges = true;
      }
    },

    reorderFormField: (state, action) => {
      const { inspectionId, fromIndex, toIndex } = action.payload;
      const inspection = state.config.inspectionDetails.find(
        (i) => i.typeId === inspectionId || i.detailType === inspectionId
      );

      if (!inspection?.formData) return;

      const [removed] = inspection.formData.splice(fromIndex, 1);
      inspection.formData.splice(toIndex, 0, removed);
      state.ui.unsavedChanges = true;
    },

    // ------------------------------------
    // Error Handling
    // ------------------------------------
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // loadConfig
      .addCase(loadConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadConfig.fulfilled, (state, action) => {
        state.loading = false;
        // Deep clone config to prevent shared references with deployedConfig
        state.config = JSON.parse(JSON.stringify(action.payload.config));
        // Deep clone deployedConfig to prevent mutations from affecting change detection
        state.deployedConfig = action.payload.deployedConfig
          ? JSON.parse(JSON.stringify(action.payload.deployedConfig))
          : null;
        // originalConfig should be the deployed config (baseline for change detection)
        // If no deployed config, use the loaded config as baseline
        state.originalConfig = state.deployedConfig
          ? JSON.parse(JSON.stringify(state.deployedConfig))
          : JSON.parse(JSON.stringify(action.payload.config));
        state.ui.workInProgress = action.payload.workInProgress;
        state.ui.unsavedChanges = false;
      })
      .addCase(loadConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // saveConfig
      .addCase(saveConfig.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveConfig.fulfilled, (state) => {
        state.saving = false;
        state.ui.workInProgress = true;
        state.ui.unsavedChanges = false;
      })
      .addCase(saveConfig.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // deployConfig
      .addCase(deployConfig.pending, (state) => {
        state.deploying = true;
        state.error = null;
      })
      .addCase(deployConfig.fulfilled, (state, action) => {
        state.deploying = false;
        state.ui.workInProgress = false;
        state.ui.unsavedChanges = false;
        // Update all config states to match the newly deployed config
        state.config = JSON.parse(JSON.stringify(action.payload.config));
        state.deployedConfig = JSON.parse(JSON.stringify(action.payload.config));
        state.originalConfig = JSON.parse(JSON.stringify(action.payload.config));
      })
      .addCase(deployConfig.rejected, (state, action) => {
        state.deploying = false;
        state.error = action.payload;
      })

      // discardPendingConfig
      .addCase(discardPendingConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(discardPendingConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.config = action.payload.config;
        state.originalConfig = JSON.parse(JSON.stringify(action.payload.config));
        state.deployedConfig = JSON.parse(JSON.stringify(action.payload.config));
        state.ui.workInProgress = false;
        state.ui.unsavedChanges = false;
      })
      .addCase(discardPendingConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Global reset
      .addCase(resetALL, () => initialState);
  },
});

// Export actions
export const {
  setActiveTab,
  selectLayout,
  selectInspection,
  selectField,
  clearSelections,
  setUnsavedChanges,
  undoChanges,
  addAssetLayout,
  updateLayout,
  updateLayoutName,
  cloneAssetLayout,
  deleteAssetLayout,
  addZone,
  updateZone,
  deleteZone,
  updateZonePosition,
  cloneZone,
  addComponent,
  updateComponent,
  deleteComponent,
  cloneComponent,
  addInspectionType,
  createInspectionType,
  updateInspection,
  deleteInspectionType,
  addFormField,
  updateFormField,
  removeFormField,
  reorderFormField,
  clearError,
} = inspectionBuilderSlice.actions;

// Export reducer
export default inspectionBuilderSlice.reducer;
